import type { ApiKey } from '../types/api'
import { formatDate, formatDateTime } from './format'

export interface LastUsedLabel {
  text: string
  title: string
  /** False for "Never" and "Not since": no use recorded while tracking. */
  recorded: boolean
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const relative = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

/** "3 days ago". Under a minute, or ahead of this clock (skew), is "Just now". */
export function relativeTime(used: Date, now: Date): string {
  const elapsed = now.getTime() - used.getTime()
  if (elapsed < MINUTE) return 'Just now'
  if (elapsed < HOUR) return relative.format(-Math.floor(elapsed / MINUTE), 'minute')
  if (elapsed < DAY) return relative.format(-Math.floor(elapsed / HOUR), 'hour')
  if (elapsed < 30 * DAY) return relative.format(-Math.floor(elapsed / DAY), 'day')
  if (elapsed < 365 * DAY) return relative.format(-Math.floor(elapsed / (30 * DAY)), 'month')
  return relative.format(-Math.floor(elapsed / (365 * DAY)), 'year')
}

/** A use before tracking started (or before a tracking gap) is not a recorded use. */
export function hasRecordedUse(lastUsedAt: string, trackedSince: string): boolean {
  return new Date(lastUsedAt) >= new Date(trackedSince)
}

/** What "Last used" shows for a key; null hides it, because the portal can't say. */
export function lastUsedLabel(
  key: Pick<ApiKey, 'last_used_at' | 'created_at'>,
  trackedSince: string | null | undefined,
  now: Date,
): LastUsedLabel | null {
  if (!trackedSince) return null
  const lastUsedAt = key.last_used_at
  if (lastUsedAt && hasRecordedUse(lastUsedAt, trackedSince)) {
    const used = new Date(lastUsedAt)
    return {
      text: relativeTime(used, now),
      title: formatDateTime(used),
      recorded: true,
    }
  }
  const title = `Use is recorded from ${formatDate(trackedSince)}, every 15 minutes`
  if (new Date(key.created_at) > new Date(trackedSince)) return { text: 'Never', title, recorded: false }
  return { text: `Not since ${formatDate(trackedSince)}`, title, recorded: false }
}
